import { createSelector } from "@reduxjs/toolkit";

import type { IShoppingCartReducerState } from "./types";
import { getPermissions, getSessionFlags } from "../../session";
import type { IShopRootState } from "../../types";

const getShoppingCartState = (state: IShopRootState): IShoppingCartReducerState => state.shopping.cart;

const isEnabled = ({ enabled }: { enabled: boolean }): boolean => enabled;

export const getCartData = createSelector(getShoppingCartState, (state) => state.cartData);
export const getSyncingCartData = createSelector(getShoppingCartState, (state) => state.syncingCartData);
export const getCartId = createSelector(getCartData, (cartData) => (cartData ? cartData.id : ""));

export const getIsCartSyncing = createSelector(getShoppingCartState, (state) => state.isSyncing);

// order positions

export const getOrderPositions = createSelector(getCartData, (cartData) => (cartData ? cartData.positions : []));

export const getEnabledOrderPositions = createSelector(getOrderPositions, (positions) => positions.filter(isEnabled));

export const getOrderPositionsCount = createSelector(getOrderPositions, (positions) => (positions ? positions.length : 0));

// request positions

export const getRequestPositions = createSelector(getCartData, (cartData) => (cartData ? cartData.onRequestPositions : []));

export const getEnabledRequestPositions = createSelector(getRequestPositions, (positions) => positions.filter(isEnabled));

export const getRequestPositionsCount = createSelector(getRequestPositions, (positions) => (positions ? positions.length : 0));

/**
 * Indicates if starting  a price request is possible
 */
export const getCanStartPriceRequest = createSelector(
  getIsCartSyncing,
  getPermissions,
  getEnabledOrderPositions,
  (isSyncing, permissions, positions) => !isSyncing && Boolean(permissions.priceRequest) && positions.length > 0
);

/**
 * Indicates if starting rfq process is possible *
 */
export const getCanStartRfq = createSelector(getIsCartSyncing, getEnabledRequestPositions, (isSyncing, positions) => !isSyncing && positions.length > 0);

export const getNumCartTransfers = createSelector(getCartData, (data) => (data ? data.numTransfers : 0));

export const getNumRemainingDeliveries = createSelector(getCartData, (data) => (data ? data.remainingChildTransactions : 0));

export const getCartItemCount = createSelector(getOrderPositionsCount, getRequestPositionsCount, (orders, requests) => orders + requests);

export const getPrevCartItemCount = createSelector(getShoppingCartState, (state) => state.prevCartItemCount);

/**
 * This selector should only be used outside the checkout.
 * In the checkout, the transaction has a sales org based setting.
 */
export const getHasProductComments = createSelector(getSessionFlags, (flags) => flags?.showPositionComment ?? false);

export const getCartSyncDate = createSelector(getCartData, (cartData) => cartData?.date ?? "");

// open cart

export const getOpenCartItemCount = createSelector(getShoppingCartState, (state) => state.openCartItemCount);
